import { Platform } from 'react-native';

/**
 * Generates a thumbnail from an existing Blob URL.
 * @param videoBlobUrl - The source object URL (e.g., 'blob:http://...')
 * @returns A Promise that resolves to a new local Blob URL for the thumbnail.
 */
export async function generateThumbnailFromBlobUrl(videoBlobUrl: string, width: number, height: number, time: number, quality: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');

    video.playsInline = true;
    video.muted = true;
    video.autoplay = false;

    // FIX 1: 'auto' is safer for seeking than 'metadata' because it actually buffers video frames
    video.preload = 'auto';

    // FIX 2: Only apply crossOrigin if it is NOT a local blob URL to avoid Safari opaque rendering bugs
    if (!videoBlobUrl.startsWith('blob:')) {
      video.crossOrigin = 'anonymous';
    }

    video.src = videoBlobUrl;

    const cleanup = () => {
      video.onloadeddata = null;
      video.onseeked = null;
      video.onerror = null;
      video.src = '';
      video.load();
      video.remove();
    };

    // FIX 3: Wait for 'loadeddata' instead of 'loadedmetadata'.
    // This ensures the first frame of video data is actively in memory.
    video.onloadeddata = () => {
      // FIX 4: Clamp the seek time.
      // If the video is shorter than the requested time, seek to its midpoint instead of the black end-frame.
      let safeTime = time;
      if (video.duration && video.duration !== Infinity) {
        // e.g., If video is 0.8s long, seek to 0.4s instead of 1s
        safeTime = Math.min(time, video.duration / 2);
      }
      video.currentTime = safeTime;
    };

    video.onseeked = () => {
      // FIX 5: The "Decoder Catch-Up" Delay.
      // Give the browser's hardware decoder 150ms to actually paint the pixels to the hidden video element before snapping the picture.
      setTimeout(() => {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          cleanup();
          return reject(new Error('Canvas context failed'));
        }

        ctx.drawImage(video, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            cleanup();
            if (blob) {
              resolve(URL.createObjectURL(blob));
            } else {
              reject(new Error('Canvas toBlob failed'));
            }
          },
          'image/jpeg',
          quality,
        );
      }, 150);
    };

    video.onerror = (e) => {
      cleanup();
      reject(new Error('Failed to generate thumbnail for video.'));
    };
  });
}

/**
 * Internal Web-Only Converter
 * Uses Web Workers to prevent UI freezing during lamejs encoding.
 */
export async function convertWebmToMp3(webmBlob: Blob): Promise<Blob> {
  try {
    if (typeof window === 'undefined') {
      throw new Error('AudioContext is not supported in this environment.');
    }
    if (Platform.OS !== 'web') {
      throw new Error('Mp3 conversion is only supported on the web');
    }

    const start = performance.now();
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    const audioContext = new AudioContextClass();

    const arrayBuffer = await webmBlob.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

    audioContext.close().catch(console.error);

    const channelData = audioBuffer.getChannelData(0);
    const sampleRate = audioBuffer.sampleRate;

    return new Promise((resolve, reject) => {
      try {
        // ✅ UNIVERSAL ROUTE: Resolves flawlessly in both Local Dev and Prod Builds!
        const worker = new Worker('/mp3-worker.js');

        worker.onmessage = (e) => {
          if (e.data.error) {
            console.error('[media-utils] Error from Worker:', e.data.error);
            worker.terminate();
            reject(new Error(e.data.error));
            return;
          }

          const blob = new Blob(e.data.mp3Data, { type: 'audio/mpeg' });
          worker.terminate();
          resolve(blob);
        };

        worker.onerror = (err) => {
          console.error('[media-utils] Worker failed initialization:', err);
          worker.terminate();
          reject(err);
        };

        const safeChannelData = new Float32Array(channelData);
        worker.postMessage({ channelData: safeChannelData, sampleRate }, { transfer: [safeChannelData.buffer] });

        console.log('[media-utils] Processing orchestration took:', performance.now() - start);
      } catch (error) {
        console.error('[media-utils] Synchronous scheduling block failed:', error);
        reject(error);
      }
    });
  } catch (e) {
    console.error(e);
    throw e;
  }
}
